'use client';

/**
 * DemoTestRunHeaderCard — three-up orientation tile for the demo canvas.
 *
 * Top-of-canvas chip strip: Actor / Script / Context. Universal mount-time
 * paint — every test_run paints the actor identity, the script summary,
 * and the user-facing context (query or loaded file). Reads slices off
 * `test_run_view` (atom-shaped BFF — one Cypher walk of the parent CV).
 *
 * The card binds to `ActorSlice`, `ScriptSlice`, `ContextSlice` shapes;
 * the same card can mount in any surface whose BFF populates those
 * slices (cf. "Cards and BFFs" doctrine in
 * `klyde_testing/docs/demo_concept.md`).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { User, FlaskConical, FileText, Search } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { TestRunView, ActorSlice, ScriptSlice, ContextSlice } from './demo-test-run-types';


function initials(email: string): string {
  if (!email) return '·';
  const local = email.split('@')[0] || email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return local.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}


export function DemoTestRunHeaderCard() {
  const T = useCardTypography();
  const simulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);

  const { data, isLoading } = useQuery<TestRunView>({
    queryKey: ['test_run_view', simulationId],
    queryFn: async () => {
      const res = await api.getStrategyData<TestRunView>('test_run_view', {
        simulation_id: simulationId,
      });
      if (!res.success) throw new Error(res.error || 'test_run_view failed');
      return res.data;
    },
    enabled: Boolean(simulationId),
    staleTime: 30_000,
  });

  if (!simulationId) return null;

  const actor: ActorSlice | undefined = data?.actor;
  const script: ScriptSlice | undefined = data?.script;
  const context: ContextSlice | null | undefined = data?.context;

  return (
    <div className="grid grid-cols-3 gap-3 mb-4">
      <Tile
        kicker="Actor"
        icon={<User className="w-3.5 h-3.5" />}
      >
        {isLoading ? (
          <SkeletonRows />
        ) : actor ? (
          <div className="flex items-center gap-2">
            <div className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center',
              'bg-primary/15 text-primary font-medium text-xs',
            )}>
              {initials(actor.display_name || actor.actor_email)}
            </div>
            <div className="min-w-0">
              <div className={cn(T.body, 'text-xs font-medium truncate')}>
                {actor.display_name || actor.actor_email || '·'}
              </div>
              <div className={cn(T.metadata, 'text-muted-foreground text-[10px] truncate')}>
                {actor.tenant_sid ? `${actor.tenant_sid} · ` : ''}{actor.role || 'service'}
              </div>
            </div>
          </div>
        ) : (
          <Placeholder />
        )}
      </Tile>

      <Tile
        kicker="Script"
        icon={<FlaskConical className="w-3.5 h-3.5" />}
      >
        {isLoading ? (
          <SkeletonRows />
        ) : script && script.case_id ? (
          <div>
            <div className={cn(T.body, 'text-xs font-medium truncate')}>
              {script.case_id}
            </div>
            <div className={cn(T.metadata, 'text-muted-foreground text-[10px]')}>
              {script.scene_count} scene{script.scene_count === 1 ? '' : 's'}
            </div>
          </div>
        ) : (
          <Placeholder />
        )}
      </Tile>

      <Tile
        kicker={context?.file_name ? 'File' : 'Query'}
        icon={
          context?.file_name
            ? <FileText className="w-3.5 h-3.5" />
            : <Search className="w-3.5 h-3.5" />
        }
      >
        {isLoading ? (
          <SkeletonRows />
        ) : context && (context.query || context.file_name) ? (
          <div>
            <div className={cn(T.body, 'text-xs font-medium font-mono truncate')}>
              {context.query || context.file_name}
            </div>
            <div className={cn(T.metadata, 'text-muted-foreground text-[10px]')}>
              {context.query ? 'user-typed' : 'fixture'}
            </div>
          </div>
        ) : (
          <Placeholder />
        )}
      </Tile>
    </div>
  );
}


interface TileProps {
  kicker: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function Tile({ kicker, icon, children }: TileProps) {
  const T = useCardTypography();
  return (
    <div className="bg-muted/40 rounded-xl px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1.5 text-muted-foreground">
        {icon}
        <span className={cn(T.metadata, 'text-[10px] uppercase tracking-wide')}>{kicker}</span>
      </div>
      {children}
    </div>
  );
}


function SkeletonRows() {
  return (
    <div className="space-y-1.5">
      <div className="h-3 w-2/3 bg-muted-foreground/15 rounded" />
      <div className="h-2 w-1/3 bg-muted-foreground/10 rounded" />
    </div>
  );
}


function Placeholder() {
  const T = useCardTypography();
  return (
    <div className={cn(T.metadata, 'text-muted-foreground/50 text-xs')}>—</div>
  );
}
